import { z } from "zod";
import { MAX_DATA_SOURCE_LOAD_CONCURRENCY } from "@erp/shared-types";
import { defineStepConfig } from "../../registry/helpers";

const startDataSourceLoadConfigSchema = z.object({
  dataSourceId: z
    .uuid()
    .describe("Data source whose links the batch direct load loads"),
  linkIds: z
    .array(z.uuid())
    .min(1)
    .optional()
    .describe(
      "Load only these of the data source's loadable links. Omit to load every loadable link.",
    ),
  concurrency: z
    .number()
    .int()
    .positive()
    .max(MAX_DATA_SOURCE_LOAD_CONCURRENCY)
    .optional()
    .describe("Links loaded at once. One at a time when omitted."),
  skipUnchangedRows: z
    .boolean()
    .optional()
    .describe(
      "Leave ingest rows whose values already match untouched and out of the transform, so a reload only re-derives the products of the rows that changed. True when omitted; false for a full load that re-derives every product.",
    ),
  sqlMode: z
    .boolean()
    .optional()
    .describe(
      "Experimental: load each file in SQL mode, merging the whole file in the database and transforming only the rows that changed (every row when skipUnchangedRows is false). A link that does not qualify falls back to the chunked load. False when omitted.",
    ),
});

const startDataSourceLoadOutputSchema = z.object({
  started: z
    .boolean()
    .describe(
      "Whether the batch started. False when a direct load was already running in the organization.",
    ),
  batchId: z
    .string()
    .optional()
    .describe("The started batch's workflow id, when it started"),
  runId: z
    .string()
    .optional()
    .describe("The started batch's run id, when it started"),
  linkIds: z
    .array(z.uuid())
    .optional()
    .describe("The links the batch loads, in load order, when it started"),
  reason: z
    .string()
    .optional()
    .describe("Why the batch did not start, when it did not"),
});

/**
 * Start Data Source Load
 *
 * Starts a batch direct load of a data source (the same batch as "Load all"
 * on the data source page) and ends at once: the step does not wait for the
 * loads, which run as their own workflow and show on the Data Source Loads
 * page. Put it after a Schedule Trigger to load a data source on a schedule.
 * When a direct load is already running in the organization the batch is
 * refused, and the step ends with `started: false` and the reason instead of
 * failing the run.
 */
export const startDataSourceLoadStep = defineStepConfig({
  type: "start_data_source_load",
  name: "Start Data Source Load",
  description:
    "Start a batch direct load of a data source's links (item master first, then the other product schemas, then the rest) and end without waiting for it. The loads show on the Data Source Loads page. If a direct load is already running in the organization, the step does not start another and returns started: false with the reason.",
  category: "Data Ingest",
  libraryModule: "ItemMaster",

  inputSchema: z.object({}),

  outputSchema: startDataSourceLoadOutputSchema,

  configSchema: startDataSourceLoadConfigSchema,

  emits: [] as const,
});
